/**
 * The Workshop's system dialog: the kit Modal with its four pegs, a Barlow
 * title, one line saying what goes and what stays, and its own two labels
 * (never a generic OK). On a phone it is a sheet floating 8px in from the
 * sides and the bottom, the primary action on top.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { cn } from "@/utils/helpers";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What goes and what stays, in one or two sentences. */
  description?: string;
  /** A short mono line under the body, e.g. "This cannot be undone". */
  consequence?: string;
  children?: ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmTone?: "danger" | "primary";
  confirmIcon?: LucideIcon;
  onConfirm?: () => void;
  /** Render the body and actions as a form; the confirm button submits it. */
  onSubmit?: () => void;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  /** A quiet line at the start of the footer (desk) or above the buttons (phone). */
  footnote?: string;
  /** Replaces the two buttons, e.g. a single "Done". */
  actions?: ReactNode;
  className?: string;
  /** The element the dialog grows out of (and closes back into). */
  growFrom?: () => Element | null;
  /** Dialog / Form: the line runs the full width under the title row, then the fields. */
  form?: boolean;
};

export default function WorkshopDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  consequence,
  children,
  cancelLabel,
  confirmLabel,
  confirmTone = "primary",
  confirmIcon,
  onConfirm,
  onSubmit,
  confirmDisabled = false,
  confirmLoading = false,
  footnote,
  actions,
  className,
  growFrom,
  form = false,
}: Props) {
  // The caller clears what the dialog is about as it closes; the words stay
  // on screen until the close has finished animating.
  const [shown, setShown] = useState({ title, description, consequence });
  if (
    isOpen &&
    (shown.title !== title ||
      shown.description !== description ||
      shown.consequence !== consequence)
  ) {
    setShown({ title, description, consequence });
  }
  const words = isOpen ? { title, description, consequence } : shown;
  const hasBody = Boolean(children) || Boolean(words.consequence) || (form && Boolean(words.description));
  const buttons = actions ?? (
    <>
      {cancelLabel ? (
        <KitButton
          type="button"
          variant="ghost"
          className="ws-dialog-btn"
          onClick={() => onOpenChange(false)}
        >
          {cancelLabel}
        </KitButton>
      ) : null}
      {confirmLabel ? (
        <KitButton
          type={onSubmit ? "submit" : "button"}
          variant={confirmTone === "danger" ? "danger" : "primary"}
          iconLeft={confirmIcon}
          className="ws-dialog-btn"
          isDisabled={confirmDisabled}
          loading={confirmLoading}
          onClick={onSubmit ? undefined : onConfirm}
        >
          {confirmLabel}
        </KitButton>
      ) : null}
    </>
  );

  const inner = (
    <>
      {hasBody ? (
        <div className="ws-dialog-body">
          {form && words.description ? <p className="ws-dialog-intro">{words.description}</p> : null}
          {children}
          {words.consequence ? <p className="ws-dialog-consequence">{words.consequence}</p> : null}
        </div>
      ) : null}
      <div className="ws-dialog-foot">
        {footnote ? <p className="ws-dialog-footnote">{footnote}</p> : null}
        <div className="ws-dialog-actions">{buttons}</div>
      </div>
    </>
  );

  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={words.title}
      description={form ? undefined : words.description}
      modalClassName={cn("ws-dialog", form && "ws-dialog--form", className)}
      overlayClassName="ws-dialog-overlay"
      growFrom={growFrom}
    >
      <span className="ws-sheet-grabber" aria-hidden="true" />
      {onSubmit ? (
        <form
          className="ws-dialog-form"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (confirmDisabled || confirmLoading) return;
            onSubmit();
          }}
        >
          {inner}
        </form>
      ) : (
        inner
      )}
    </KitModal>
  );
}
